/**
 * `useOrgMembers` — live read of every joined member in the org,
 * joined with their public profile (display name + phone + photoURL).
 *
 * Web port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/org/useOrgMembers.ts
 *
 * The full member list is the union of:
 *   - `organizations/{orgId}.ownerId`         → always present, role = superAdmin
 *   - `organizations/{orgId}.memberIds[]`     → paid roles (admin/manager/…)
 *   - keys of `organizations/{orgId}.roles{}` → also catches clients
 *
 * For each uid we read `organizations/{orgId}/memberPublic/{uid}` for
 * the display fields (the only read path Firestore rules allow for
 * cross-user profile data — `users/{uid}` is owner-only).
 *
 * Missing memberPublic docs (race window after invite, before the
 * projection trigger runs) get safe "Member" placeholders so the
 * UI never breaks.
 */
import {
  collection,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/lib/firebase';
import type { RoleKey } from '@/lib/useProjectMembers';

export type OrgMember = {
  uid: string;
  displayName: string;
  phoneNumber: string | null;
  email: string | null;
  photoURL: string | null;
  role: RoleKey;
  /** True when this is the org owner (always superAdmin). */
  isOwner: boolean;
  /** True when the member appears in `clientUids[]` of any project
   *  (computed elsewhere — present here only as a slot the caller can
   *  fill from a separate `useProjects` read). */
  joinedAtMs: number | null;
};

export type UseOrgMembersResult = {
  members: OrgMember[];
  loading: boolean;
  error: string | null;
};

const VALID_ROLES = new Set<RoleKey>([
  'superAdmin',
  'admin',
  'manager',
  'accountant',
  'siteEngineer',
  'supervisor',
  'designer',
  'viewer',
  'client',
]);

async function fetchMemberPublic(
  orgId: string,
  uids: string[],
): Promise<Map<string, Record<string, unknown>>> {
  if (uids.length === 0) return new Map();
  // Firestore `in` queries support max 30 ids per call — chunk.
  const out = new Map<string, Record<string, unknown>>();
  for (let i = 0; i < uids.length; i += 30) {
    const slice = uids.slice(i, i + 30);
    try {
      const snap = await getDocs(
        query(
          collection(db, 'organizations', orgId, 'memberPublic'),
          where(documentId(), 'in', slice),
        ),
      );
      snap.forEach((d) => out.set(d.id, d.data() as Record<string, unknown>));
    } catch (e) {
      console.warn('[useOrgMembers] memberPublic chunk fetch failed:', e);
    }
  }
  return out;
}

export function useOrgMembers(
  orgId: string | null | undefined,
): UseOrgMembersResult {
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, 'organizations', orgId),
      async (snap) => {
        if (!snap.exists()) {
          setMembers([]);
          setLoading(false);
          return;
        }
        const data = snap.data() as {
          ownerId?: string;
          memberIds?: string[];
          roles?: Record<string, string>;
        };
        const ownerId = data.ownerId ?? null;
        const memberIds = Array.isArray(data.memberIds) ? data.memberIds : [];
        const rolesMap = (data.roles ?? {}) as Record<string, string>;

        // Union of every uid known to the org doc.
        const uidSet = new Set<string>();
        if (ownerId) uidSet.add(ownerId);
        for (const uid of memberIds) uidSet.add(uid);
        for (const uid of Object.keys(rolesMap)) uidSet.add(uid);
        const uids = Array.from(uidSet);

        try {
          const pub = await fetchMemberPublic(orgId, uids);
          const out: OrgMember[] = uids.map((uid) => {
            const raw = pub.get(uid) ?? {};
            const roleRaw =
              uid === ownerId ? 'superAdmin' : (rolesMap[uid] ?? 'viewer');
            const role = VALID_ROLES.has(roleRaw as RoleKey)
              ? (roleRaw as RoleKey)
              : 'viewer';
            const displayName =
              typeof raw.displayName === 'string' && raw.displayName.trim()
                ? (raw.displayName as string)
                : typeof raw.fullName === 'string' && raw.fullName.trim()
                  ? (raw.fullName as string)
                  : typeof raw.phoneNumber === 'string'
                    ? (raw.phoneNumber as string)
                    : 'Member';
            const joinedAtMs =
              raw.joinedAt &&
              typeof (raw.joinedAt as { toMillis?: () => number }).toMillis ===
                'function'
                ? (raw.joinedAt as { toMillis: () => number }).toMillis()
                : null;
            return {
              uid,
              displayName,
              phoneNumber:
                typeof raw.phoneNumber === 'string' ? raw.phoneNumber : null,
              email: typeof raw.email === 'string' ? raw.email : null,
              photoURL:
                typeof raw.photoURL === 'string' && raw.photoURL.trim().length > 0
                  ? (raw.photoURL as string)
                  : null,
              role,
              isOwner: uid === ownerId,
              joinedAtMs,
            };
          });
          // Sort: owner first, then by displayName.
          out.sort((a, b) => {
            if (a.isOwner && !b.isOwner) return -1;
            if (!a.isOwner && b.isOwner) return 1;
            return a.displayName.localeCompare(b.displayName, undefined, {
              sensitivity: 'base',
            });
          });
          setMembers(out);
          setLoading(false);
        } catch (e) {
          setError((e as Error).message);
          setLoading(false);
        }
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
    return unsub;
  }, [orgId]);

  return { members, loading, error };
}

// ───── Role badge tone — keyed by role ─────

export const ROLE_TONE: Record<RoleKey, { bg: string; fg: string }> = {
  superAdmin:   { bg: '#FEE2E2', fg: '#B91C1C' }, // red — owner
  admin:        { bg: '#F3E8FF', fg: '#7C3AED' }, // purple
  manager:      { bg: '#DBEAFE', fg: '#2563EB' }, // blue
  accountant:   { bg: '#D1FAE5', fg: '#059669' }, // green
  siteEngineer: { bg: '#FEF3C7', fg: '#B45309' }, // amber
  supervisor:   { bg: '#FFEDD5', fg: '#C2410C' }, // orange
  designer:     { bg: '#FCE7F3', fg: '#DB2777' }, // pink
  viewer:       { bg: '#F1F5F9', fg: '#475569' }, // slate
  client:       { bg: '#E0F2FE', fg: '#0369A1' }, // sky
};

/** Short two-letter avatar from a display name. */
export function memberInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
